import { useEffect, useMemo, useState } from "react";
import { HELP_REQUEST_MAX_ATTACHMENTS, HELP_REQUEST_MAX_ATTACHMENTS_MEGABYTES } from "../constants/helpRequests";
import { useAuthContext } from "../context/authContext";
import de from "../i18n/de";
import en from "../i18n/en";
import es from "../i18n/es";
import fr from "../i18n/fr";
import it from "../i18n/it";
import { type LanguageCode } from "../types";

export type Translation = typeof en;
export type TranslationKeys = keyof Translation;

export const ts: { [key in LanguageCode]: Translation } = {
  en,
  it,
  de,
  es,
  fr,
  ar: en,
  cs: en,
  da: en,
  el: en,
  et: en,
  fi: en,
  hr: en,
  hu: en,
  is: en,
  ja: en,
  lt: en,
  lv: en,
  nl: en,
  no: en,
  pl: en,
  pt: en,
  ru: en,
  sk: en,
  sl: en,
  sv: en,
  tr: en,
  zh: en,
  kr: en,
};

export type I18n = {
  translation: Translation;
  errorsMap: Map<number, string>;
};

const useI18n = (): I18n => {
  const auth = useAuthContext();
  const [translation, setTranslation] = useState<Translation>(ts[auth.identity?.language ?? en.languageCode]);

  useEffect(() => {
    auth.identity?.language && setTranslation(ts[auth.identity.language] ?? en);
  }, [auth.identity?.language]);

  // Memoized per language: this hook runs in most components, and a new map (and i18n object) on every
  // render made it useless as a dependency and rebuilt ~150 entries each time.
  const errorsMap = useMemo(
    () =>
      new Map<number, string>([
        // projects
        [100001, translation.projects.errors.deleteProjectWithConfigurationsNotPossible],
        [100002, translation.projects.errors.notFound],
        [100004, translation.projects.errors.sharingNotFound],
        [100005, translation.projects.errors.documentNotFound],
        [100006, translation.projects.errors.missingDocumentName],
        [100007, translation.projects.errors.documentAlreadyPresent],
        [100008, translation.projects.errors.missingProjectStatus],
        [100009, translation.projects.errors.deleteProjectWithDocumentsNotPossible],
        [100021, translation.projects.errors.inviteReadNotAllowed],
        [100022, translation.projects.errors.inviteInsertNotAllowed],
        [100023, translation.projects.errors.inviteUpdateNotAllowed],
        [100024, translation.projects.errors.inviteDeleteNotAllowed],
        [100025, translation.projects.errors.inviteAlreadyExists],
        [100026, translation.projects.errors.inviteNotFound],
        [100027, translation.projects.errors.inviteAlreadyCancelled],
        [100028, translation.projects.errors.inviteAlreadyAccepted],
        [100029, translation.projects.errors.inviteAlreadyRejected],
        [100030, translation.projects.errors.sharingAlreadyExists],
        [100031, translation.sharingRequests.errors.notFound],
        [100032, translation.sharingRequests.errors.readNotAllowed],
        [100034, translation.sharingRequests.errors.alreadyExists],
        [100035, translation.sharingRequests.errors.alreadyShared],
        [100036, translation.sharingRequests.errors.alreadyAccepted],
        [100037, translation.sharingRequests.errors.alreadyRejected],

        // users
        [110001, translation.users.errors.notFound],
        [110007, translation.users.errors.alreadyExists],
        [7100002, translation.users.errors.pendingAlreadyExists],

        // calculations
        [120001, translation.calculations.errors.notFound],
        [120014, translation.calculations.errors.moduleNotSupported],
        [120016, translation.calculations.errors.inviteInsertNotAllowed],
        [120017, translation.calculations.errors.inviteUpdateNotAllowed],
        [120018, translation.calculations.errors.inviteDeleteNotAllowed],
        [120019, translation.calculations.errors.inviteAlreadyExists],
        [120020, translation.calculations.errors.inviteNotFound],
        [120021, translation.calculations.errors.inviteAlreadyCancelled],
        [120022, translation.calculations.errors.inviteAlreadyAccepted],
        [120023, translation.calculations.errors.inviteAlreadyRejected],
        [120024, translation.calculations.errors.moduleRestricted],
        [120025, translation.calculations.errors.sharingNotFound],
        [120031, translation.calculations.errors.sharingAlreadyExists],
        [120032, translation.sharingRequests.errors.notFound],
        [120033, translation.sharingRequests.errors.readNotAllowed],
        [120035, translation.sharingRequests.errors.alreadyExists],
        [120036, translation.sharingRequests.errors.alreadyShared],
        [120037, translation.sharingRequests.errors.alreadyAccepted],
        [120038, translation.sharingRequests.errors.alreadyRejected],

        // items
        [1300001, translation.items.errors.notFound],
        [1300003, translation.items.errors.missingCode],

        // clients
        [1600001, translation.clients.errors.notFound],
        [1600003, translation.clients.errors.insertNotAllowed],
        [1600004, translation.clients.errors.readNotAllowed],
        [1600005, translation.clients.errors.updateNotAllowed],
        [1600006, translation.clients.errors.deleteNotAllowed],

        // secrets
        [1700002, translation.secrets.errors.missingValue],

        // categories
        [1800001, translation.categories.errors.notFound],
        [1800003, translation.categories.errors.insertNotAllowed],
        [1800004, translation.categories.errors.readNotAllowed],
        [1800005, translation.categories.errors.updateNotAllowed],
        [1800006, translation.categories.errors.deleteNotAllowed],

        // modules
        [1900001, translation.modules.errors.notFound],
        [1900003, translation.modules.errors.insertNotAllowed],
        [1900004, translation.modules.errors.readNotAllowed],
        [1900005, translation.modules.errors.updateNotAllowed],
        [1900006, translation.modules.errors.deleteNotAllowed],
        [1900007, translation.geoBlocking.errors.moduleBlocked],
        // kept out by the module's access control: the same text, users are never told why
        [1900008, translation.geoBlocking.errors.moduleBlocked],
        [1900009, translation.geoBlocking.errors.moduleBlocked],

        // concrete resin - chemical anchor mappings
        [2000001, translation.concreteResinModule.errors.mappingNotFound],
        [2000007, translation.concreteResinModule.errors.mappingAlreadyPresent],

        // concrete resin - chemical anchors
        [2100001, translation.concreteResinModule.errors.anchorNotFound],
        [2100007, translation.concreteResinModule.errors.anchorInUseByMapping],
        [2100008, translation.concreteResinModule.errors.anchorInUseByEntry],
        [2100009, translation.common.errorInvalidVisibility],
        [2100010, translation.concreteResinModule.errors.anchorAlreadyPresent],

        // deck designer - screw families
        [2200003, translation.deckDesignerModule.screwFamilies.errors.nameAlreadyPresent],
        [2200004, translation.deckDesignerModule.screwFamilies.errors.inUseByScrew],
        [2200005, translation.deckDesignerModule.screwFamilies.errors.inUseByConnectorFamily],
        [2200006, translation.deckDesignerModule.screwFamilies.errors.inUseBySupportFamily],
        [2200007, translation.common.errorInvalidVisibility],

        // deck designer - grooving types
        [2300003, translation.deckDesignerModule.groovingTypes.errors.nameAlreadyPresent],

        // deck designer - fastening types
        [2400007, translation.deckDesignerModule.fasteningTypes.errors.nameAlreadyPresent],

        // deck designer - connector materials
        [2500003, translation.deckDesignerModule.connectorMaterials.errors.nameAlreadyPresent],
        [2500004, translation.deckDesignerModule.connectorMaterials.errors.inUseByConnector],

        // deck designer - substructure families
        [2600003, translation.deckDesignerModule.substructureFamilies.errors.nameAlreadyPresent],
        [2600004, translation.common.errorInvalidVisibility],

        // deck designer - configurations
        [2700003, translation.deckDesignerModule.configurations.errors.invalidTotalHeight],
        [2700016, translation.deckDesignerModule.configurations.errors.connectorFamilyNotAvailable],
        [2700017, translation.deckDesignerModule.configurations.errors.connectorNotAvailable],
        [2700018, translation.deckDesignerModule.configurations.errors.screwNotAvailable],
        [2700019, translation.deckDesignerModule.configurations.errors.supportFamilyNotAvailable],
        [2700020, translation.deckDesignerModule.configurations.errors.supportNotAvailable],
        [2700021, translation.deckDesignerModule.configurations.errors.substructureNotAvailable],

        // deck designer - connectors
        [2800002, translation.deckDesignerModule.connectors.errors.itemAlreadyPresent],
        [2800003, translation.deckDesignerModule.connectors.errors.screwAlreadyPresent],
        [2800004, translation.deckDesignerModule.connectors.errors.inUseByConfiguration],
        [2800005, translation.common.errorInvalidVisibility],

        // deck designer - connector families
        [2900003, translation.deckDesignerModule.connectorFamilies.errors.nameAlreadyPresent],
        [2900004, translation.common.errorInvalidVisibility],

        // deck designer - screws
        [3100002, translation.deckDesignerModule.screws.errors.itemAlreadyPresent],
        [3100003, translation.deckDesignerModule.screws.errors.inUseByConfiguration],
        [3100004, translation.deckDesignerModule.screws.errors.inUseByConnector],
        [3100005, translation.common.errorInvalidVisibility],

        // deck designer - substructures
        [3200002, translation.deckDesignerModule.substructures.errors.itemAlreadyPresent],
        [3200003, translation.deckDesignerModule.substructures.errors.baseHeightAlreadyPresent],
        [3200004, translation.deckDesignerModule.substructures.errors.inUseByConfiguration],
        [3200005, translation.common.errorInvalidVisibility],

        // deck designer - substructure materials
        [3300003, translation.deckDesignerModule.substructureMaterials.errors.nameAlreadyPresent],

        // deck designer - substructure material classes
        [3400005, translation.deckDesignerModule.substructureMaterialClasses.errors.nameAlreadyPresent],
        [3400006, translation.deckDesignerModule.substructureMaterialClasses.errors.inUseByConfiguration],
        [3400007, translation.deckDesignerModule.substructureMaterialClasses.errors.inUseBySupport],

        // deck designer - supports
        [3500002, translation.deckDesignerModule.supports.errors.itemAlreadyPresent],
        [3500003, translation.deckDesignerModule.supports.errors.inUseByConfiguration],
        [3500004, translation.common.errorInvalidVisibility],

        // deck designer - support families
        [3600003, translation.deckDesignerModule.supportFamilies.errors.nameAlreadyPresent],
        [3600004, translation.common.errorInvalidVisibility],

        // deck designer - secondary items
        [3700003, translation.deckDesignerModule.secondaryItems.errors.inUseByConfiguration],
        [3700004, translation.deckDesignerModule.secondaryItems.errors.inUseByConnectorMapping],

        // beam reinforcement - screw families
        [3800010, translation.beamReinforcementModule.screwFamilies.errors.nameAlreadyPresent],

        // concrete resin - calculation entries
        [3900001, translation.concreteResinModule.errors.entryMissingChemicalAnchor],
        [3900002, translation.concreteResinModule.errors.entryIncompleteCalculation],
        [3900003, translation.concreteResinModule.errors.anchorNotAvailable],
        [3900004, translation.concreteResinModule.errors.invalidRodDiameter],
        [3900005, translation.concreteResinModule.errors.invalidHoleDiameter],

        // envelope designer
        [4000002, translation.envelopeDesignerModule.errors.invalidMaterial],
        [4000003, translation.envelopeDesignerModule.errors.materialNotFound],
        [4000004, translation.envelopeDesignerModule.errors.customMaterialNotFound],
        [4000005, translation.envelopeDesignerModule.errors.invalidLayer],
        [4000007, translation.envelopeDesignerModule.errors.invalidBridge],
        [4000009, translation.envelopeDesignerModule.errors.condCategoryNotFound],
        [4000011, translation.envelopeDesignerModule.errors.materialAlreadyExists],
        [4000012, translation.envelopeDesignerModule.errors.materialInUse],
        [4000013, translation.envelopeDesignerModule.errors.exampleNotFound],
        [4000014, translation.envelopeDesignerModule.errors.invalidExample],
        [4000015, translation.envelopeDesignerModule.errors.exampleAlreadyExists],
        [4000019, translation.envelopeDesignerModule.errors.categoryAlreadyExists],
        [4000020, translation.envelopeDesignerModule.errors.categoryInUseByMaterial],
        [4000021, translation.envelopeDesignerModule.errors.categoryInUseBySubcategory],
        [4000024, translation.envelopeDesignerModule.errors.subcategoryAlreadyExists],
        [4000025, translation.envelopeDesignerModule.errors.subcategoryInUseByMaterial],

        // access control
        [4100001, translation.accessControl.errors.forbidden],
        [4100002, translation.accessControl.errors.forbidden],
        [4100003, translation.accessControl.errors.ruleNotFound],
        [4100004, translation.accessControl.errors.ruleMissingEmail],
        [4100005, translation.accessControl.errors.ruleInvalidStatus],
        [4100006, translation.accessControl.errors.ruleCannotTargetSelf],
        [4100007, translation.accessControl.errors.settingsReadNotAllowed],
        [4100008, translation.accessControl.errors.settingsUpdateNotAllowed],
        [4100009, translation.accessControl.errors.ruleReadNotAllowed],
        [4100010, translation.accessControl.errors.ruleInsertNotAllowed],
        [4100011, translation.accessControl.errors.ruleDeleteNotAllowed],
        [4100012, translation.accessControl.errors.lastAdmin],
        [4100013, translation.accessControl.errors.invalidScope],
        [4100016, translation.accessControl.errors.forbidden],
        [4100017, translation.accessControl.errors.invalidModule],
        // help requests
        [4200001, translation.helpRequests.errors.invalidForm],
        [4200002, translation.helpRequests.errors.missingSubject],
        [4200003, translation.helpRequests.errors.subjectTooLong],
        [4200004, translation.helpRequests.errors.missingMessage],
        [4200005, translation.helpRequests.errors.messageTooLong],
        [4200006, translation.helpRequests.errors.tooManyAttachments(HELP_REQUEST_MAX_ATTACHMENTS)],
        [4200007, translation.helpRequests.errors.attachmentsTooLarge(HELP_REQUEST_MAX_ATTACHMENTS_MEGABYTES)],
        [4200008, translation.helpRequests.errors.invalidScreenshot],
        [4200009, translation.helpRequests.errors.invalidDiagnostics],
        // feedback
        [4300001, translation.feedbacks.errors.sendNotAllowed],
        [4300002, translation.feedbacks.errors.invalidType],
        // usage statistics
        [4400001, translation.usageStatistics.errors.readNotAllowed],
        [4400002, translation.usageStatistics.errors.invalidPeriod],

        // us lifting calculator - calculations
        [4500001, translation.usLiftingCalculatorModule.errors.calculationInvalidInput],
        [4500002, translation.usLiftingCalculatorModule.errors.calculationNotAvailable],
        [4500008, translation.usLiftingCalculatorModule.errors.uploadInvalidWorkbook],
        [4500010, translation.usLiftingCalculatorModule.errors.uploadFileTooLarge],
        [4500011, translation.usLiftingCalculatorModule.errors.uploadNoWorksheet],
        [4500012, translation.usLiftingCalculatorModule.errors.uploadNoHeader],
        [4500013, translation.usLiftingCalculatorModule.errors.uploadMetricTemplate],
        [4500014, translation.usLiftingCalculatorModule.errors.uploadMissingColumns],
        [4500015, translation.usLiftingCalculatorModule.errors.uploadNoRows],
        // us lifting calculator - device families
        [4600003, translation.common.errorInvalidVisibility],
        [4600004, translation.usLiftingCalculatorModule.errors.deviceFamilyNameAlreadyExists],

        // us lifting calculator - devices
        [4700001, translation.usLiftingCalculatorModule.errors.deviceNotFound],
        [4700002, translation.usLiftingCalculatorModule.errors.deviceAlreadyExists],
        [4700004, translation.usLiftingCalculatorModule.errors.deviceInUseByLift],
        [4700005, translation.common.errorInvalidVisibility],

        // us lifting calculator - screw families

        // us lifting calculator - screws
        [4900001, translation.usLiftingCalculatorModule.errors.screwNotFound],
        [4900002, translation.usLiftingCalculatorModule.errors.screwAlreadyExists],
        [4900004, translation.usLiftingCalculatorModule.errors.screwInUseByLift],
        [4900005, translation.common.errorInvalidVisibility],

        // us lifting calculator - lift types
        [5100003, translation.common.errorInvalidVisibility],

        // us lifting calculator - specific gravities
        [5200001, translation.usLiftingCalculatorModule.errors.specificGravityNotFound],
        [5200002, translation.usLiftingCalculatorModule.errors.specificGravityAlreadyExists],
        [5200005, translation.usLiftingCalculatorModule.errors.specificGravityInUseByLift],
        [5200006, translation.common.errorInvalidVisibility],

        // us lifting calculator - capacities
        [5400001, translation.usLiftingCalculatorModule.errors.capacityNotFound],
        [5400002, translation.usLiftingCalculatorModule.errors.capacityAlreadyExists],

        // us lifting calculator - device screw mappings
        [5500001, translation.usLiftingCalculatorModule.errors.deviceScrewMappingNotFound],
        [5500002, translation.usLiftingCalculatorModule.errors.deviceScrewMappingAlreadyExists],

        // us lifting calculator - device family lift types
        [5600001, translation.usLiftingCalculatorModule.errors.deviceFamilyLiftTypeNotFound],
        [5600002, translation.usLiftingCalculatorModule.errors.deviceFamilyLiftTypeAlreadyExists],
        [5600003, translation.usLiftingCalculatorModule.errors.deviceFamilyLiftTypeInvalidLimits],

        // us lifting calculator - lift type anchor counts
        [5700001, translation.usLiftingCalculatorModule.errors.liftTypeAnchorCountNotFound],
        [5700002, translation.usLiftingCalculatorModule.errors.liftTypeAnchorCountAlreadyExists],

        // geo-blocking
        [5800006, translation.geoBlocking.errors.ruleAlreadyExists],
        [5800013, translation.geoBlocking.errors.blocksSelf],
        [5900003, translation.geoBlocking.errors.regionNameAlreadyExists],
        [5900006, translation.geoBlocking.errors.regionInUse],
        [5900011, translation.geoBlocking.errors.blocksSelf],

        // screw connector - screw families
        [6200001, translation.screwConnectorModule.errors.familyNotFound],
        [6200002, translation.screwConnectorModule.errors.familyMissingName],
        [6200003, translation.screwConnectorModule.errors.familyAlreadyExists],

        // screw connector - washers
        [6300001, translation.screwConnectorModule.errors.washerNotFound],
        [6300002, translation.screwConnectorModule.errors.washerMissingCode],
        [6300003, translation.screwConnectorModule.errors.washerAlreadyExists],

        // screw connector - screw diameters
        [6400001, translation.screwConnectorModule.errors.screwDiameterNotFound],

        // screw connector - screws
        [6500001, translation.screwConnectorModule.errors.screwNotFound],
        [6500002, translation.screwConnectorModule.errors.screwAlreadyExists],

        // screw connector - timber materials
        [6600001, translation.screwConnectorModule.errors.timberMaterialNotFound],
        [6600002, translation.screwConnectorModule.errors.timberMaterialAlreadyExists],

        // screw connector - load duration classes
        [6700001, translation.screwConnectorModule.errors.loadDurationClassNotFound],
        [6700002, translation.screwConnectorModule.errors.loadDurationClassMissingCode],
        [6700003, translation.screwConnectorModule.errors.loadDurationClassAlreadyExists],

        // screw connector - service classes
        [6800001, translation.screwConnectorModule.errors.serviceClassNotFound],
        [6800002, translation.screwConnectorModule.errors.serviceClassAlreadyExists],

        // screw connector - kmod and γM values
        [6900001, translation.screwConnectorModule.errors.kmodValueNotFound],
        [7000001, translation.screwConnectorModule.errors.gammaMValueNotFound],
      ]),
    [translation],
  );

  return useMemo(() => ({ translation, errorsMap }), [errorsMap, translation]);
};

export default useI18n;
